import React from 'react';

// ── Button ──────────────────────────────────────────────────────────────────
// ALL primary buttons: red bg (#DD0000) white text
// Secondary: black bg white text
// Gold: gold bg black text
export const Button = ({ children, variant='primary', size='md', className='', disabled, loading, onClick, type='button' }) => {
  const base = 'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';
  const sizes = { sm:'px-3 py-1.5 text-xs', md:'px-4 py-2 text-sm', lg:'px-6 py-3 text-sm' };
  const variants = {
    primary:   'bg-brand-500 text-white hover:bg-brand-600 focus:ring-brand-400 shadow-sm',
    secondary: 'bg-de-black text-white hover:bg-black focus:ring-gray-600 shadow-sm',
    gold:      'bg-gold-400 text-de-black hover:bg-gold-500 focus:ring-gold-400 font-bold shadow-sm',
    danger:    'bg-brand-500 text-white hover:bg-brand-700 focus:ring-brand-400',
    ghost:     'text-de-text hover:bg-surface focus:ring-gray-300 border border-gray-300',
    success:   'bg-green-600 text-white hover:bg-green-700 focus:ring-green-400',
    outline:   'border-2 border-brand-500 text-brand-500 hover:bg-brand-500 hover:text-white focus:ring-brand-400',
  };
  return (
    <button type={type} disabled={disabled||loading} onClick={onClick}
      className={`${base} ${sizes[size]||sizes.md} ${variants[variant]||variants.primary} ${className}`}>
      {loading && <Spinner size="xs" className="mr-2" />}
      {children}
    </button>
  );
};

// ── Badge ──────────────────────────────────────────────────────────────────
export const Badge = ({ children, variant='default', size='sm' }) => {
  const base = 'inline-flex items-center gap-1 font-semibold rounded-full';
  const sizes = { xs:'px-2 py-0.5 text-xs', sm:'px-2.5 py-1 text-xs', md:'px-3 py-1.5 text-sm' };
  // Level badges: gold bg black text. Alerts: red bg white text.
  const variants = {
    default:   'bg-surface text-de-text border border-gray-200',
    locked:    'bg-gray-100 text-gray-500 border border-gray-200',
    active:    'bg-gold-400 text-de-black',          // gold bg, black text
    completed: 'bg-green-100 text-green-800 border border-green-300',
    paid:      'bg-green-100 text-green-800',
    pending:   'bg-gold-100 text-yellow-800 border border-gold-400',
    overdue:   'bg-brand-500 text-white',             // red bg white text for alerts
    info:      'bg-blue-100 text-blue-800',
    ai:        'bg-purple-100 text-purple-800',
    red:       'bg-brand-500 text-white',             // red alert badge
    black:     'bg-de-black text-white',
    gold:      'bg-gold-400 text-de-black',
  };
  return <span className={`${base} ${sizes[size]||sizes.sm} ${variants[variant]||variants.default}`}>{children}</span>;
};

export const LevelBadge = ({ level, status }) => {
  const icons    = { locked:'🔒', active:'⭐', completed:'✅' };
  const variants = { locked:'locked', active:'active', completed:'completed' };
  return <Badge variant={variants[status]||'default'} size="sm"><span>{icons[status]}</span>{level}</Badge>;
};

export const PaymentBadge = ({ status }) => {
  const map = { paid:['paid','✓ Paid'], pending:['pending','⏳ Pending'], overdue:['overdue','⚠ Overdue'] };
  const [v, label] = map[status] || ['default', status];
  return <Badge variant={v}>{label}</Badge>;
};

// ── Card ──────────────────────────────────────────────────────────────────
// Cards: white bg, light border. accent='red' → red top border (dashboard stats)
export const Card = ({ children, className='', hover=false, onClick, accent }) => (
  <div onClick={onClick}
    className={`bg-white rounded-xl border border-gray-200 shadow-sm
      ${accent==='red'  ? 'border-t-4 border-t-brand-500' : ''}
      ${accent==='gold' ? 'border-t-4 border-t-gold-400'  : ''}
      ${accent==='black'? 'border-t-4 border-t-de-black'  : ''}
      ${hover ? 'hover:shadow-md hover:border-brand-300 transition-all duration-200 cursor-pointer' : ''}
      ${className}`}>
    {children}
  </div>
);

// ── Stat Card — white with red top border ────────────────────
export const StatCard = ({ label, value, icon, color='red', delta }) => {
  const iconColors = { red:'bg-brand-50 text-brand-500', gold:'bg-gold-50 text-yellow-700', green:'bg-green-50 text-green-600', black:'bg-surface text-de-black', purple:'bg-purple-50 text-purple-600' };
  return (
    <Card className="p-5" accent="red">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</p>
          <p className="text-2xl font-bold text-de-black">{value}</p>
          {delta && <p className="text-xs text-green-600 mt-1">{delta}</p>}
        </div>
        {icon && <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${iconColors[color]||iconColors.red}`}>{icon}</div>}
      </div>
    </Card>
  );
};

// ── Modal ─────────────────────────────────────────────────────
export const Modal = ({ isOpen, onClose, title, children, size='md' }) => {
  if (!isOpen) return null;
  const sizes = { sm:'max-w-md', md:'max-w-lg', lg:'max-w-2xl', xl:'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${sizes[size]||sizes.md} animate-slide-up max-h-[90vh] flex flex-col`} onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-de-black">{title}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-surface text-gray-400 hover:text-de-black transition-colors font-bold text-lg">×</button>
        </div>
        <div className="overflow-y-auto flex-1 p-5">{children}</div>
      </div>
    </div>
  );
};

// ── Spinner ────────────────────────────────────────────────────
export const Spinner = ({ size='md', className='' }) => {
  const sizes = { xs:'w-3 h-3 border', sm:'w-4 h-4 border-2', md:'w-6 h-6 border-2', lg:'w-8 h-8 border-2', xl:'w-12 h-12 border-3' };
  return <div className={`${sizes[size]||sizes.md} border-current border-t-transparent rounded-full animate-spin ${className}`} />;
};

export const AIThinking = ({ message='AI is thinking...' }) => (
  <div className="flex flex-col items-center justify-center gap-4 py-8">
    <div className="flex items-center gap-2">
      <div className="w-2 h-2 rounded-full bg-brand-500 ai-dot" />
      <div className="w-2 h-2 rounded-full bg-brand-500 ai-dot" />
      <div className="w-2 h-2 rounded-full bg-brand-500 ai-dot" />
    </div>
    <p className="text-sm text-gray-500 font-medium">{message}</p>
  </div>
);

export const EmptyState = ({ icon='📭', title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="text-4xl mb-3">{icon}</div>
    <h3 className="text-base font-bold text-de-black mb-1">{title}</h3>
    {description && <p className="text-sm text-gray-500 mb-4">{description}</p>}
    {action}
  </div>
);

// ── Progress Bar ───────────────────────────────────────────────
export const ProgressBar = ({ value, max=100, color='red', label, showPercent=true }) => {
  const pct = Math.min(100, Math.round((value/max)*100));
  const colors = { red:'bg-brand-500', gold:'bg-gold-400', green:'bg-green-500', black:'bg-de-black' };
  return (
    <div className="w-full">
      {(label||showPercent) && (
        <div className="flex justify-between items-center mb-1">
          {label && <span className="text-xs text-gray-600">{label}</span>}
          {showPercent && <span className="text-xs font-semibold text-de-text">{pct}%</span>}
        </div>
      )}
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${colors[color]||colors.red}`} style={{width:`${pct}%`}} />
      </div>
    </div>
  );
};

export const SectionHeader = ({ title, subtitle, action }) => (
  <div className="flex items-start justify-between mb-6">
    <div>
      <h2 className="text-xl font-bold text-de-black">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    {action}
  </div>
);
